import type { ColorScheme } from '@hivewatch/dark'
import { Box, MultiSelectInput, Radio, RadioGroup } from '@hivewatch/dark'

import { FilterOptionI } from '@/utils/filterDrawers'

import { FilterRow } from './FilterRow'

interface RadioFilterRowIProps<T> {
  label: string
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<T>[]
  value: FilterOptionI<T> | null | undefined
  onChange: (value: FilterOptionI<T> | null) => void
  /** Unique name for the underlying radio input group. */
  name: string
}

/**
 * Single-value Filter Row: a MultiSelectInput chip trigger whose panel hosts
 * a RadioGroup. The selection renders as one dismissible chip; options are
 * keyed by label since values may be non-string (e.g. enum arrays).
 */
export const RadioFilterRow = <T,>({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
  name,
}: RadioFilterRowIProps<T>) => {
  const chipOptions = options.map((option) => ({
    value: option.label,
    label: option.label,
  }))

  const handleRadioChange = (selectedLabel: string) => {
    const option = options.find((o) => o.label === selectedLabel)
    onChange(option ?? null)
  }

  return (
    <FilterRow colorScheme={colorScheme} label={label}>
      <MultiSelectInput
        colorScheme={colorScheme}
        onClear={() => onChange(null)}
        onRemove={() => onChange(null)}
        options={chipOptions}
        panelContent={
          <Box p='12px'>
            <RadioGroup
              aria-label={label}
              colorScheme={colorScheme}
              name={name}
              onChange={handleRadioChange}
              value={value?.label ?? null}
            >
              {options.map((option) => (
                <Radio
                  key={option.label}
                  label={option.label}
                  value={option.label}
                />
              ))}
            </RadioGroup>
          </Box>
        }
        placeholder={placeholder}
        selected={value ? [value.label] : []}
      />
    </FilterRow>
  )
}
